import React, { useState, useEffect } from 'react';
import { bool, oneOfType, object, string, shape } from 'prop-types';
import { FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import {
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_CLOSED,
  LISTING_STATE_DRAFT,
  LISTING_STATE_PUBLISHED,
  propTypes,
} from '../../util/types';
import { NamedLink, Modal, PrimaryButton, SecondaryButton, StripePaymentModal } from '../../components';
import EditIcon from './EditIcon';
import { verifiedSubscription, confirmListingPayment, getSellerViewStats } from '../../util/api';
import { lifetimeViews } from '../../util/listingViews';
import { isEmbeddedPaymentEnabled } from '../../util/stripeEmbedded';

import css from './ListingPage.module.css';
import { useDispatch } from 'react-redux';
import { closeListing, openListing } from '../ManageListingsPage/ManageListingsPage.duck';

import { ReactComponent as ViewIcon } from './viewIcon.svg';

const FavoriteIcon = ({ style }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" style={style} aria-hidden="true">
    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
  </svg>
);

const DeleteOutlineIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" />
  </svg>
);

const RocketLaunchIcon = ({ style }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" style={style} aria-hidden="true">
    <path d="M13.13 22.19l-1.63-3.83c1.57-.58 3.04-1.36 4.4-2.27l-2.77 6.1zM5.64 12.5l-3.83-1.63 6.1-2.77C7 9.46 6.22 10.93 5.64 12.5zM19.22 4c.28 0 .53.02.74.06.04.21.06.46.06.74 0 2.72-1.17 7.19-4.89 10.91-1.77 1.77-3.74 3.14-5.76 4.09l-3.04-3.04c.96-2.03 2.34-4 4.09-5.75C14.04 5.32 18.5 4 19.22 4zm0-2c-.67 0-5.7.1-10.25 4.64-2.18 2.18-3.65 4.43-4.61 6.47L2 14l4.91 2.13L8.77 18l2.13 4.91.88-2.37c2.03-.96 4.28-2.43 6.46-4.61 4.54-4.54 4.64-9.58 4.64-10.25 0-.66-.1-2.68-2.88-2.68zm-6.24 8.96c-.78.78-2.05.78-2.83 0-.78-.78-.78-2.05 0-2.83s2.05-.78 2.83 0c.78.78.78 2.05 0 2.83z" />
  </svg>
);


export const ActionBarMaybe = props => {
  const {
    rootClassName,
    className,
    isOwnListing,
    listing,
    editParams,
    showNoPayoutDetailsSet,
    onManageDisableScrolling,
    sessionURL,
    sessionId,
    payListingBoostedPaymentSessionURL,
    onPayListingBoostedPayment,
  } = props;
  const classes = classNames(rootClassName || css.actionBar, className);
  const dispatch = useDispatch()
  const [isBoostlistingModalOpen, setIsBoostlistingModalOpen] = useState(false);
  const [boostLoading, setBoostLoading] = useState(false);
  const embeddedPaymentEnabled = isEmbeddedPaymentEnabled();
  const [boostClientSecret, setBoostClientSecret] = useState(null);
  const [boostModalOpen, setBoostModalOpen] = useState(false);



  const { carRegistrationNumber: classicRegNumber, regPlate, internalComment, totalViews, totalLikes, listingType, isDeleted, isTradein, isHide} = listing?.attributes?.publicData || {};
  // P25009-485 BR-2 — the owner's own marker; the page 404s for everyone else.
  const isHiddenTradein = isTradein === true && isHide === true;
  // Listings from the new wizard before P25009-459 only carry `regPlate`.
  const carRegistrationNumber = classicRegNumber || regPlate;

  const { tireId, isBoosted } = listing?.attributes?.metadata || {};
  const isPuplished = listing.attributes.state === LISTING_STATE_PUBLISHED;
  const currentStock = listing.currentStock?.attributes?.quantity;
  const isOutOfStock = currentStock === 0;
  const id = listing.id.uuid;
  const state = listing.attributes.state;
  const isPendingApproval = state === LISTING_STATE_PENDING_APPROVAL;
  const isClosed = state === LISTING_STATE_CLOSED;
  const isDraft = state === LISTING_STATE_DRAFT;

  const handleVerifiedSubscription = async (sessionId) => {
    try {
      const response = await verifiedSubscription({ sessionId });
      // console.log(response);

      return response;
    } catch (e) { console.log(e); }
  }

  // Shared post-payment finalisation — applied identically whether the payment
  // was verified via the hosted redirect return or the embedded modal onSuccess.
  // `stripeRef` is { sessionId } or { paymentIntentId }; the server reads the
  // boost payment from Stripe and switches the boost on.
  const applyVerifiedPayment = async (response, stripeRef) => {
    if (!response?.success) return;

    if (response.paymentMode === "payment") {
      if (response.paymentType === "listing_boosted_fee" && listing?.id?.uuid === response.listingId) {
        try {
          await confirmListingPayment({ listingId: response.listingId, ...stripeRef });
        } catch (e) {
          console.error('Boost payment could not be applied', e);
        }
      }

      setIsBoostlistingModalOpen(false);
    }
    const url = new URL(window.location.href);
    url.searchParams.delete("sessionId");
    window.history.replaceState({}, document.title, url.toString());

    // Reload page after success
    window.location.reload();
  };

  /* The owner's lifetime views: the old counter stopped counting visitors on
   * 2026-09-03, so the listing's running total is added to it
   * (src/util/listingViews.js). */
  const ownListingId = isOwnListing ? listing?.id?.uuid : null;
  const [recordedViews, setRecordedViews] = useState(null);
  useEffect(() => {
    if (!ownListingId) return undefined;
    let cancelled = false;
    getSellerViewStats()
      .then(stats => {
        if (!cancelled) {
          setRecordedViews(stats?.recordedViewsByListing?.[ownListingId] || 0);
        }
      })
      .catch(() => {
        // The counter alone is still a number worth showing.
      });
    return () => {
      cancelled = true;
    };
  }, [ownListingId]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Embedded flow has no hosted redirect / sessionId return — the modal's
    // onSuccess drives finalisation instead (BR-01).
    if (embeddedPaymentEnabled) return;

    const redirectURL = sessionURL || payListingBoostedPaymentSessionURL;
    if (redirectURL) {
      window.open(redirectURL, "_self");
      return;
    }

    const verifyPayment = async () => {
      if (!sessionId) return;
      const response = await handleVerifiedSubscription(sessionId);
      await applyVerifiedPayment(response, { sessionId });
    };

    verifyPayment();
  }, [sessionURL, payListingBoostedPaymentSessionURL]);


  const handleBoostPayment = async () => {
    setBoostLoading(true); // start loading

    try {
      const res = await onPayListingBoostedPayment({ listingId: listing.id?.uuid });

      // Embedded: swap the info modal for the in-page Payment Element modal.
      if (embeddedPaymentEnabled && res?.clientSecret) {
        setBoostClientSecret(res.clientSecret);
        setIsBoostlistingModalOpen(false);
        setBoostModalOpen(true);
      }
      // Hosted: the redirect is handled by the useEffect watching the redux URL.
    } catch (error) {
      console.error("Boost failed:", error);

    } finally {
      setBoostLoading(false);
    }
  };


  if (isOwnListing && showNoPayoutDetailsSet) {
    return (
      <div className={classes}>
        <p className={classNames(css.ownListingText, css.missingPayoutDetailsText)}>
          <FormattedMessage id="ListingPage.addPayoutDetailsMessage" />
        </p>
        <NamedLink className={css.addPayoutDetails} name="StripePayoutPage">
          <EditIcon className={css.editIcon} />
          <FormattedMessage id="ListingPage.addPayoutDetails" />
        </NamedLink>
      </div>
    );
  } else if (isOwnListing) {
    let ownListingTextTranslationId = 'ListingPage.ownListing';
    /* P25009-410 BR-4 — a catalogue row is not an annons: it is on the
     * seller's profile, not on the marketplace. */
    const isCatalogue = listing?.attributes?.publicData?.visibility === 'catalog';

    if (isCatalogue && !isClosed && !isDraft) {
      ownListingTextTranslationId = 'ListingPage.ownListingCatalogue';
    } else if (isPendingApproval) {
      ownListingTextTranslationId = 'ListingPage.ownListingPendingApproval';
    } else if (isClosed) {
      ownListingTextTranslationId = 'ListingPage.ownClosedListing';
    } else if (isDraft) {
      ownListingTextTranslationId = 'ListingPage.ownListingDraft';
    }

    const message = isDraft ? 'ListingPage.finishListing' : 'ListingPage.editListing';

    const ownListingTextClasses = classNames(css.ownListingText, {
      [css.ownListingTextPendingApproval]: isPendingApproval,
    });

    return (
      <div className={classes}>
        <div className={css.ownListingInfoContainer}>
          <p className={ownListingTextClasses}>
            <FormattedMessage id={ownListingTextTranslationId} />
            {isPuplished && !isOutOfStock && listingType !== 'buying' && !showNoPayoutDetailsSet ? <div className={css.boostContainer}>
              <PrimaryButton className={css.boostButton}
                disabled={isBoosted}
                onClick={() => setIsBoostlistingModalOpen(true)}>
                <RocketLaunchIcon style={{ width: 16, height: 16, marginRight: 4 }} />
                {isBoosted ? 'Boostade' : 'Boosts'} </PrimaryButton>
            </div> : null}
          </p>
          {/* P25009-512 BR-3 — the listing number alone opens the box too, so the
            * owner can always read it out to a customer or to support. */}
          {tireId || carRegistrationNumber || internalComment || isHiddenTradein ? <div className={css.ownListingInfoWrapper}>
            {isHiddenTradein ? <div className={css.ownListingTradein}>Inbyte · dold</div> : null}
            {carRegistrationNumber ? <div className={css.ownListingInfo}>{carRegistrationNumber}</div> : null}
            {internalComment ? <div className={css.ownListingInfo}>{internalComment}</div> : null}
            {tireId ? <div className={css.ownListingInfo}>{tireId}</div> : null}
          </div> : null}
        </div>
        <div className={css.combinedActionButtonWrapper}>
          <a className={css.editListingLink} href={`/annonser/skapa?edit=${id}`}>
            <EditIcon className={css.editIcon} />
            <FormattedMessage id={message} />
          </a>
        {isDeleted ? <div className={css.deleted}>Raderad annons</div>:<div className={css.editListingLink}
            onClick={() => {
              if (isClosed) {
                dispatch(openListing(listing.id.uuid))
              } else {
                dispatch(closeListing(listing.id.uuid))
              }
              window.location.reload()
            }}
          >
            <DeleteOutlineIcon className={css.muiIcon} />
            <FormattedMessage id={isClosed ? "ActionBarMaybe.openListing" : "ActionBarMaybe.closeListing"} />
          </div>}  
          <div className={css.totalViews}>
            <ViewIcon /> {lifetimeViews({ totalViews }, recordedViews)}

            <FavoriteIcon style={{ color: '#FF10BC', width: 18, height: 18 }} />
            {totalLikes || 0}
          </div>
        </div>
        <Modal
          id="boostListing"
          isOpen={isBoostlistingModalOpen}
          onClose={() => setIsBoostlistingModalOpen(false)}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div className={css.modalContent}>
            <h2 className={css.modalTitle}>Boosta din annons</h2>

            <p className={css.description}>
              Boosta din annons för att få fler visningar och snabbare svar. Din annons
              kommer att lyftas fram och visas på toppositioner i <strong>7 dagar</strong>.
            </p>

            <div className={css.benefits}>
              <ul className={css.benefitList}>
                <li>Toppplacering på startsidan</li>
                <li>Ökad synlighet i sökresultat</li>
                <li>Visas i karusellen för boostade annonser</li>
                <li>Aktiv i 15 dagar</li>
              </ul>
            </div>

            <div className={css.priceBox}>
              <span className={css.priceLabel}>Pris:</span>
              <span className={css.priceValue}>99 kr</span>
            </div>

            <div className={css.actions}>
              <PrimaryButton
                type="button"
                onClick={handleBoostPayment}
                inProgress={boostLoading}
                disabled={boostLoading}
                className={css.payButton}>
                Betala och boosta
              </PrimaryButton>

              <SecondaryButton
                className={css.cancelButton}
                onClick={() => setIsBoostlistingModalOpen(false)}
              >
                Avbryt
              </SecondaryButton>
            </div>
          </div>

        </Modal>
        {embeddedPaymentEnabled ? (
          <StripePaymentModal
            isOpen={boostModalOpen}
            clientSecret={boostClientSecret}
            title="Boost this Listing"
            submitLabel="Pay"
            returnUrl={`${typeof window !== 'undefined' ? window.location.origin : ''}/l/${id}?payment_status=complete`}
            onClose={() => {
              setBoostModalOpen(false);
              setBoostClientSecret(null);
            }}
            onSuccess={async paymentIntent => {
              const response = await verifiedSubscription({
                embedded: true,
                paymentIntentId: paymentIntent?.id,
              });
              setBoostModalOpen(false);
              setBoostClientSecret(null);
              await applyVerifiedPayment(response, { paymentIntentId: paymentIntent?.id });
            }}
          />
        ) : null}
      </div>
    );
  } else if (isClosed) {
    return (
      <div className={classes}>
        <p className={css.closedListingText}>
          <FormattedMessage id="ListingPage.closedListing" />
        </p>
      </div>
    );
  }
  return null;
};
ActionBarMaybe.defaultProps = {
  rootClassName: null,
  className: null,
};

ActionBarMaybe.propTypes = {
  rootClassName: string,
  className: string,
  isOwnListing: bool.isRequired,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]).isRequired,
  editParams: shape({
    id: string,
    slug: string,
    type: string,
    tab: string,
  }),
};

ActionBarMaybe.displayName = 'ActionBarMaybe';

export default ActionBarMaybe;
